import React from 'react';
import { Coins } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../ui/tooltip';

/**
 * Compact AI-credit badge for the Astrid drawer header.
 *
 * Shows the wallet balance as CREDITS (never dollars). Tone shifts to amber
 * when running low (< $3) and rose when out of credits (< $0.10). Clicking it
 * opens the top-up dialog. Super Admin / Owner see a neutral tone — they are
 * exempt from credit gating.
 */
export const WalletBadge = ({ wallet, staff, low, blocked, onTopUp, personaName = 'Astrid' }) => {
  if (!wallet) return null;
  const credits = Math.max(0, Math.round(wallet.balance_credits ?? 0));

  const tone = staff
    ? 'border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-100'
    : blocked
    ? 'border-rose-300 bg-rose-50 text-rose-700 hover:bg-rose-100'
    : low
    ? 'border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100'
    : 'border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100';

  const tip = staff
    ? `Org wallet: ${credits} credits. Super Admin / Owner sessions don't spend credits.`
    : blocked
    ? `Out of AI credits (${credits} left). Top up from $5 to keep using ${personaName}.`
    : low
    ? `Running low — ${credits} credits left. Top up from $5 to keep ${personaName} working.`
    : `${credits} AI credits available — powers ${personaName}, reports, vendor emails & portal setup.`;

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={onTopUp}
            data-testid="ai-wallet-badge"
            className={`inline-flex items-center gap-1 h-7 px-2 rounded-full border text-[11px] font-semibold transition-colors ${tone}`}
            aria-label="AI credits — top up"
          >
            <Coins className="h-3.5 w-3.5" data-testid="ai-wallet-badge-icon" />
            <span data-testid="ai-wallet-badge-credits">{credits}</span>
            <span className="opacity-60 hidden sm:inline">cr</span>
          </button>
        </TooltipTrigger>
        <TooltipContent
          side="bottom"
          align="end"
          className="max-w-[260px] text-[11px] leading-snug p-2.5"
          data-testid="ai-wallet-badge-tooltip"
        >
          {tip}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default WalletBadge;
